import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { useAuth } from './auth/useAuth';
import './App.css';
import Layout from './components/Layout';
import NoHeaderLayout from './components/NoHeaderLayout';
import ConsultationLayout from './components/ConsultationLayout';
import MainPage from './pages/MainPage';
import AnalysisPage from './pages/AnalysisPage';
import ReportDetailPage from './pages/ReportDetailPage';
import AppReportDetailPage from './pages/AppReportDetailPage';
import ActionPlansByReportPage from './pages/ActionPlansByReportPage';
import ReportsListPage from './pages/ReportsListPage';
import ActionPlanLogsPage from './pages/ActionPlanLogsPage';
import UserTimelineConsolePage from './pages/UserTimelineConsolePage';
import IcsnConsolePage from './pages/IcsnConsolePage';
import ChatConsultPage from './pages/ChatConsultPage';
import GatewayTraceConsolePage from './pages/GatewayTraceConsolePage';
import RetailIntelligenceConsolePage from './pages/RetailIntelligenceConsolePage';
import RetailIntelligenceShoppingPage from './pages/RetailIntelligenceShoppingPage';
import RetailIntelligenceAdminPage from './pages/RetailIntelligenceAdminPage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import ReviewCaseDetailPage from './pages/ReviewCaseDetailPage';
import AppLoginPage from './pages/AppLoginPage';
import MyDataConsentPage from './pages/MyDataConsentPage';
import MyDataGuidePage from './pages/MyDataGuidePage';
import MyDataFetchingPage from './pages/MyDataFetchingPage';
import MyDataAnalysisReadyPage from './pages/MyDataAnalysisReadyPage';
import MyDataErrorPage from './pages/MyDataErrorPage';
import AppAnalysisRunningPage from './pages/AppAnalysisRunningPage';
import NhisDashboardPage from './pages/NhisDashboardPage';
import SignupStep1Page from './pages/SignupStep1Page';
import SignupStep2Page from './pages/SignupStep2Page';
import SignupStep3Page from './pages/SignupStep3Page';
import LoginPage from './pages/LoginPage';
import ConsultationDashboardPage from './pages/LifePlanner/ConsultationDashboardPage';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function RoutePendingScreen({ message = '회원 정보를 확인하는 중입니다.' }) {
  return (
    <div
      style={{
        minHeight: '40vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#4b5563',
        fontSize: 15,
      }}
    >
      {message}
    </div>
  );
}

function ProtectedRoute({ children }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const targetPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (auth.isUnauthenticated) {
      auth.actions.rememberIntent({
        kind: 'web',
        path: targetPath,
        reason: 'protected-route',
        createdAt: Date.now(),
      });
      navigate('/', {
        replace: true,
        state: { from: location },
      });
    }
  }, [auth.actions, auth.isUnauthenticated, location, navigate, targetPath]);

  if (auth.isBooting) {
    return <RoutePendingScreen />;
  }

  if (!auth.isAuthenticated) {
    return <RoutePendingScreen message="로그인이 필요합니다. 로그인 화면으로 이동합니다." />;
  }

  return children;
}

function ProtectedRouteApp({ children }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const targetPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (auth.isUnauthenticated) {
      auth.actions.rememberIntent({
        kind: 'app',
        path: targetPath,
        reason: 'protected-app-route',
        createdAt: Date.now(),
      });
      navigate('/app/login', { replace: true });
    }
  }, [auth.actions, auth.isUnauthenticated, navigate, targetPath]);

  if (auth.isBooting) {
    return <RoutePendingScreen message="앱 세션을 확인하는 중입니다." />;
  }

  if (!auth.isAuthenticated) {
    return <RoutePendingScreen message="앱 로그인이 필요합니다. 로그인 화면으로 이동합니다." />;
  }

  return children;
}

function AdminRoute({ children }) {
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (auth.isAuthenticated && auth.adminStatus === 'unknown') {
      void auth.actions.ensureAdmin();
    }
  }, [auth.actions, auth.adminStatus, auth.isAuthenticated, location.pathname]);

  if (auth.isBooting) {
    return <RoutePendingScreen />;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (auth.adminStatus === 'unknown' || auth.adminStatus === 'checking') {
    return <RoutePendingScreen message="권한을 확인하는 중입니다." />;
  }

  if (auth.adminStatus !== 'granted') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function ConsultationLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/consultation${location.search || ''}`} replace />;
}

function AppRoutes() {
  const auth = useAuth();
  const layoutContext = useMemo(() => {
    return {
      isLoggedIn: auth.isAuthenticated,
      isAdmin: auth.isAdmin || auth.adminStatus === 'granted',
      adminStatus: auth.adminStatus,
    };
  }, [auth.adminStatus, auth.isAdmin, auth.isAuthenticated]);

  return (
    <Routes>
      <Route path="/" element={<Layout context={layoutContext} />}>
        <Route index element={<MainPage />} />
        <Route path="analysis" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
        <Route path="analysis/dashboard" element={<ProtectedRoute><NhisDashboardPage /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute><ReportsListPage /></ProtectedRoute>} />
        <Route path="reports/:reportId" element={<ProtectedRoute><ReportDetailPage /></ProtectedRoute>} />
        <Route path="action-plans/report/:reportId" element={<ProtectedRoute><ActionPlansByReportPage /></ProtectedRoute>} />
        <Route path="reports/:reportId/logs" element={<ProtectedRoute><ActionPlanLogsPage /></ProtectedRoute>} />
        <Route path="consult/chat" element={<ProtectedRoute><ChatConsultPage /></ProtectedRoute>} />
        <Route
          path="review"
          element={<AdminRoute><ReviewQueuePage /></AdminRoute>}
        />
        <Route
          path="review/:caseId"
          element={<AdminRoute><ReviewCaseDetailPage /></AdminRoute>}
        />
        <Route
          path="console/identity"
          element={<AdminRoute><UserTimelineConsolePage /></AdminRoute>}
        />
        <Route
          path="console/users/:userNo/timeline"
          element={<AdminRoute><UserTimelineConsolePage /></AdminRoute>}
        />
        <Route
          path="console/icsn"
          element={<AdminRoute><IcsnConsolePage /></AdminRoute>}
        />
        <Route
          path="console/gateway-trace"
          element={<AdminRoute><GatewayTraceConsolePage /></AdminRoute>}
        />
        <Route
          path="retail-intelligence"
          element={<ProtectedRoute><RetailIntelligenceShoppingPage /></ProtectedRoute>}
        />
        <Route
          path="console/retail-intelligence"
          element={<ProtectedRoute><RetailIntelligenceConsolePage /></ProtectedRoute>}
        />
        <Route
          path="console/retail-intelligence/admin"
          element={<AdminRoute><RetailIntelligenceAdminPage /></AdminRoute>}
        />
        <Route path="consultation" element={<ProtectedRoute><ConsultationLayout /></ProtectedRoute>}>
          <Route index element={<ConsultationDashboardPage />} />
          <Route path=":userNo" element={<ConsultationLegacyRedirect />} />
        </Route>
        <Route path="mydata/connect/consent" element={<ProtectedRoute><MyDataConsentPage /></ProtectedRoute>} />
        <Route path="mydata/connect/guide" element={<ProtectedRoute><MyDataGuidePage /></ProtectedRoute>} />
        <Route path="mydata/connect/fetching" element={<ProtectedRoute><MyDataFetchingPage /></ProtectedRoute>} />
        <Route path="mydata/connect/analysis-ready" element={<ProtectedRoute><MyDataAnalysisReadyPage /></ProtectedRoute>} />
        <Route path="mydata/connect/error" element={<ProtectedRoute><MyDataErrorPage /></ProtectedRoute>} />
        <Route path="analysis/running" element={<ProtectedRoute><AppAnalysisRunningPage /></ProtectedRoute>} />
      </Route>

      <Route path="/app" element={<NoHeaderLayout />}>
        <Route path="login" element={<AppLoginPage />} />
        <Route path="analysis" element={<ProtectedRouteApp><MainPage /></ProtectedRouteApp>} />
        <Route path="analysis/running" element={<ProtectedRouteApp><AppAnalysisRunningPage /></ProtectedRouteApp>} />
        <Route path="analysis/dashboard" element={<ProtectedRouteApp><NhisDashboardPage /></ProtectedRouteApp>} />
        <Route path="reports" element={<ProtectedRouteApp><ReportsListPage /></ProtectedRouteApp>} />
        <Route path="reports/:reportId" element={<ProtectedRouteApp><AppReportDetailPage /></ProtectedRouteApp>} />
        <Route path="action-plans/report/:reportId" element={<ProtectedRouteApp><ActionPlansByReportPage /></ProtectedRouteApp>} />
        <Route path="reports/:reportId/logs" element={<ProtectedRouteApp><ActionPlanLogsPage /></ProtectedRouteApp>} />
        <Route path="mydata/connect/consent" element={<ProtectedRouteApp><MyDataConsentPage /></ProtectedRouteApp>} />
        <Route path="mydata/connect/guide" element={<ProtectedRouteApp><MyDataGuidePage /></ProtectedRouteApp>} />
        <Route path="mydata/connect/fetching" element={<ProtectedRouteApp><MyDataFetchingPage /></ProtectedRouteApp>} />
        <Route path="mydata/connect/analysis-ready" element={<ProtectedRouteApp><MyDataAnalysisReadyPage /></ProtectedRouteApp>} />
        <Route path="mydata/connect/error" element={<ProtectedRouteApp><MyDataErrorPage /></ProtectedRouteApp>} />
      </Route>

      <Route path="/login" element={<NoHeaderLayout />}>
        <Route index element={<LoginPage />} />
      </Route>

      <Route path="/signup" element={<NoHeaderLayout />}>
        <Route index element={<Navigate to="step1" replace />} />
        <Route path="step1" element={<SignupStep1Page />} />
        <Route path="step2" element={<SignupStep2Page />} />
        <Route path="step3" element={<SignupStep3Page />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
